import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

import { cn } from "@/lib/utils";

export type ToastTone = "error" | "success" | "info";

interface ToastCustomProps {
  id: string | number;
  tone: ToastTone;
  title: string;
  description?: string;
  onClose: () => void;
}

const ICONS = {
  error: AlertCircle,
  success: CheckCircle2,
  info: Info,
};

const ACCENT_GRADIENT: Record<ToastTone, string> = {
  success:
    "linear-gradient(180deg, var(--color-accent-blue) 0%, var(--color-accent-orange) 100%)",
  error: "linear-gradient(180deg, #dc2626 0%, #991b1b 100%)",
  info: "var(--color-accent-blue)",
};

const ICON_COLOR: Record<ToastTone, string> = {
  success: "var(--color-accent-blue)",
  error: "#dc2626",
  info: "var(--color-accent-blue)",
};

export function ToastCustom({
  tone,
  title,
  description,
  onClose,
}: ToastCustomProps) {
  const Icon = ICONS[tone];

  return (
    <motion.div
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 8, opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "relative flex w-full items-start gap-3 overflow-hidden rounded-lg border border-white/10 bg-white/5 p-3 pl-4 shadow-2xl backdrop-blur-xl",
        "min-w-[280px] max-w-sm"
      )}
    >
      <span
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: ACCENT_GRADIENT[tone] }}
        aria-hidden
      />

      <Icon
        className="mt-0.5 size-4 shrink-0"
        style={{ color: ICON_COLOR[tone] }}
      />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && (
          <p className="mt-0.5 text-xs text-foreground/70">{description}</p>
        )}
      </div>

      <button
        onClick={onClose}
        className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
        aria-label="Cerrar"
      >
        <X className="size-3.5" />
      </button>
    </motion.div>
  );
}
